import { Window } from "@dhx/ts-window";
import { DataPage, DataStore } from "@xbs/muon";
import { Toolbar } from "@dhx/ts-toolbar";
import { ISelection, SpreadsheetEvents } from "../types";
import { NavigationBarEvents } from "@dhx/ts-navbar";
import { IValue } from "@xbs/muon/dist/types/types";
import { isDefined } from "@dhx/ts-common/core";
import { getCellNameById } from "../helpers/main";
import { awaitRedraw } from "@dhx/ts-common/dom";
import { WindowEvents } from "@dhx/ts-window/sources/types";
import { getFormattedValue } from "../helpers/format";
import { IEventSystem } from "@dhx/ts-common/events";
import { Spreadsheet } from "../Spreadsheet";
import { Grid } from "@dhx/ts-grid";

export class SearchForm {
	protected _handlers: { [key: string]: (...args) => void };
	private _toolbar: Toolbar;
	private _modal: Window;
	private _selection: ISelection;
	private _page: DataPage;
	private _data: [number, number, IValue][];
	private _filtered: [number, number, IValue][];
	private _stepIndex: number;
	private _searchText: string;
	private _events: IEventSystem<SpreadsheetEvents>;
	private _spreadsheet: Spreadsheet;
	private _grid: Grid;

	constructor(spreadsheet: Spreadsheet) {
		this._events = spreadsheet.events;
		this._spreadsheet = spreadsheet;
		this._grid = (spreadsheet as any)._grid;

		this._selection = spreadsheet.selection;
		this._modal = new Window({
			css: "dhx-spreadsheet-form-search-modal",
			height: 56,
			left: window.innerWidth - 385,
			viewportOverflow: true,
			top: 0,
			width: 405
		});
		this._toolbar = new Toolbar(null, {
			data: [
				{
					type: "input",
					value: "",
					placeholder: "Search",
					icon: "dxi dxi-magnify",
					id: "search",
					css: "search-input",
					width: 200
				},
				{
					type: "title",
					value: "",
					id: "results",
				},
				{ icon: "dxi dxi-chevron-up", id: "prev" },
				{ icon: "dxi dxi-chevron-down", id: "next" },
				{
					type: "separator",
				},
				{ icon: "dxi dxi-close", id: "close" },
			],
			css: "dhx-spreadsheet-form-search-form"
		});

		this._modal.attach(this._toolbar);
		this._initHandlers();
	}

	hide() {
		if (this._modal.isVisible()) {
			this._modal.hide();
			(this._selection as any)._removeSearchCell();
		}
	}

	research(page: DataPage) {
		if (page !== this._page) {
			this._page = page;
		}
		this._data = this._page.serialize().map(item => {
			const [row, col, meta] = item;
			const { value, format } = meta;
			return [row, col, { ...meta, value: getFormattedValue(value, format) }];
		});
		this.search(this._searchText);
	}

	show(page: DataPage) {
		this.research(page);
		this._modal.show();
		this._toolbar.setState({ search: "" });
		this._toolbar.disable(["next", "prev"]);
	}

	isVisible() {
		return this._modal.isVisible();
	}

	search(text: string, page?: DataPage) {
		if (page && this._page !== page) {
			this.research(page)
		}
		if (this._toolbar.getState().search !== text) {
			this._searchText = text;
			this._toolbar.setState({ search: text });
		}
		this._filtered = [];
		if (text) {
			this._filtered = this._data.filter(
				([_row, _col, { value }]) =>
				!this._grid.isRowHidden(_row + 1) &&
					(!text || (isDefined(value) && `${value}`.toLowerCase().includes(text.toLowerCase())))
			);
		}
		const searchedCells = this._filtered.map(cell => getCellNameById((this._spreadsheet as any)._grid, cell[0] + 1, cell[1] + 1, ));
		if (!this._modal.isVisible()) return searchedCells;
		(this._selection as any)._setSearchedArray(searchedCells);
		this._stepIndex = 0;
		this.next();
		if (text) {
			this._toolbar.enable(["next", "prev"]);
		} else {
			this._toolbar.disable(["next", "prev"]);
			(this._selection as any)._removeSearchCell();
		}
		return searchedCells;
	}

	next() {
		const length = this._filtered.length;
		this._toolbar.data.update("results", { value: `${length ? this._stepIndex + 1 : 0}/${length}` });
		this._updateWindowPos();
		if (!length) return;
		this._stepIndex++;
		if (this._stepIndex >= length) {
			this._stepIndex = 0;
		}

		(this._selection as any)._setSearchCell(this._stepIndex);
	}
	prev() {
		const length = this._filtered.length;
		this._toolbar.data.update("results", { value: `${length ? this._stepIndex + 1 : 0}/${length}` });
		this._updateWindowPos();
		if (!length) return;
		this._stepIndex--;
		if (this._stepIndex < 0) {
			this._stepIndex = length - 1;
		}
		this._toolbar.data.update("results", { value: `${this._stepIndex + 1}/${length}` });
		(this._selection as any)._setSearchCell(this._stepIndex);
	}

	destructor() {
		document.removeEventListener("keydown", this._keyHandler);
	}

	private _updateWindowPos() {
		awaitRedraw().then(() => {
			const size = this._modal.getSize();
			this._modal.setPosition(window.innerWidth - size.width - 1, 0);
		});
	}

	private _keyHandler(e: KeyboardEvent) {
		if (e.code === "Escape") {
			e.preventDefault();
			this.hide();
		}

		if (e.code === "KeyG") {
			const ctrl = e.ctrlKey || e.metaKey;
			if (ctrl && !e.shiftKey) {
				e.preventDefault();
				this.next();
			}
			if (ctrl && e.shiftKey) {
				e.preventDefault();
				this.prev();
			}
		}
	}

	protected _initHandlers() {
		this._toolbar.events.on(NavigationBarEvents.click, id => {
			switch (id) {
				case "prev":
					this.prev();
					break;
				case "close":
					this.hide();
					break;
				case "next":
					this.next();
					break;
			}
		});
		this._toolbar.events.on(NavigationBarEvents.inputChange, (_id, text) => {
			this._searchText = text;
			this.search(text);
		});
		this._modal.events.on(WindowEvents.afterShow, () => {
			awaitRedraw().then(() => {
				const input = this._toolbar.getRootNode().querySelector("input");
				input?.focus();
				input?.setAttribute("autocomplete", "off")
				this._toolbar.select("search");
			});
		});
		document.addEventListener("keydown", this._keyHandler.bind(this));
		this._events.on(SpreadsheetEvents.afterSheetChange, () => {
			if (this.isVisible()) {
				this.research((this._spreadsheet as any)._activePage);
			}
		});
		this._events.on(SpreadsheetEvents.afterAction, () => {
			if (this.isVisible()) {
				this.research((this._spreadsheet as any)._activePage);
			}
		})
	}
}
